import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CreditCard, Loader2, ShieldCheck, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiClient, apiError, beDate, btnPrimary, btnGhost } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

const INCLUDED = [
  "Dossiers de chantier illimités",
  "Assistant vocal et lecture de plaque",
  "Devis PDF, envoi et signature en ligne",
  "Rapport de visite pour le client",
  "Mode hors ligne sur chantier",
  "Export Billit / Yuki / Odoo / CSV",
  "Tous les comptes de vos ouvriers inclus",
  "Sans engagement, résiliable à tout moment",
];

export default function StudioSubscription() {
  const [state, setState] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");

  const load = useCallback(() => {
    studio.get("/subscription").then(({ data }) => setState(data)).catch(() => {});
    studio.get("/subscription/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  }, []);

  const downloadInvoice = async (invoice) => {
    try {
      const res = await studio.get(`/subscription/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "Téléchargement impossible."));
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(poll);
          toast.success("Abonnement activé. Merci !");
          setParams({});
          load();
        }
      } catch (e) { /* on continue de patienter */ }
      if (tries > 12) clearInterval(poll);
    }, 2500);
    return () => clearInterval(poll);
  }, [sessionId, load, setParams]);

  const subscribe = async () => {
    setBusy("checkout");
    try {
      const { data } = await apiClient.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Paiement indisponible. Réessayez."));
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await studio.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e, "Gestion indisponible."));
      setBusy("");
    }
  };

  const active = state?.status === "active";
  const expired = state?.status === "expired";

  return (
    <StudioLayout title="Mon abonnement" subtitle={`Devisto Studio : ${state?.price_label ? state.price_label.split(" / ")[0] : "25 €"} par mois pour toute votre société, ouvriers inclus.`}>
      {!state ? (
        <PageSkeleton />
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div data-testid="subscription-state" className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">État</p>
            <p className="mt-3 font-heading text-2xl font-bold text-[#0F2645]">
              {active ? "Abonnement actif" : expired ? "Essai terminé" : `Essai — ${state.trial_days_left} jour(s) restant(s)`}
            </p>
            <p className="mt-2 text-sm text-[#5C6A87] leading-relaxed">
              {active
                ? state.current_period_end
                  ? `Prochaine échéance le ${beDate(state.current_period_end.slice(0, 10))}.`
                  : "Votre abonnement est en cours."
                : expired
                  ? "Activez l'abonnement pour préparer de nouveaux devis. Vos dossiers restent accessibles."
                  : `Essai gratuit jusqu'au ${state.trial_ends_at ? beDate(state.trial_ends_at.slice(0, 10)) : "—"}, sans carte bancaire.`}
            </p>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-heading text-4xl font-extrabold text-[#0F2645]">{state?.price_label ? state.price_label.split(" / ")[0] : "25 €"}</span>
              <span className="text-sm text-[#5C6A87]">/ mois HTVA par société</span>
            </p>
            {state.is_owner ? (
              <div className="mt-7 flex flex-wrap gap-3">
                {!active && (
                  <button type="button" data-testid="subscribe-btn" disabled={!!busy} onClick={subscribe} className={btnPrimary}>
                    {busy === "checkout" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Activer mon abonnement
                  </button>
                )}
                {active && (
                  <button type="button" data-testid="portal-btn" disabled={!!busy} onClick={openPortal} className={btnGhost}>
                    {busy === "portal" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                    Gérer / résilier
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-7 text-xs text-[#5C6A87]">
                Seul le responsable de la société gère l'abonnement.
              </p>
            )}
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-[#5C6A87]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" /> Paiement sécurisé par Stripe
            </p>
          </div>

          <div className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">Inclus</p>
            <ul className="mt-4 space-y-3">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#10B981]/10 shrink-0">
                    <Check className="w-3 h-3 text-[#10B981]" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-[#5C6A87] leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!!invoices.length && (
        <div data-testid="invoices-card" className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#3A5CC8]" /> Mes factures
          </h2>
          <p className="mt-1 text-xs text-[#5C6A87]">
            Facturé par Devisto · TVA BE 0786.236.171
          </p>
          <div className="mt-4 space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} data-testid={`invoice-${inv.number}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E7F7] px-4 py-3">
                <span className="text-sm font-bold text-[#0F2645]">{inv.number}</span>
                <span className="text-xs text-[#5C6A87]">{beDate(inv.issued_at)}</span>
                <span className="text-xs text-[#5C6A87]">
                  {inv.amount_excl.toFixed(2).replace(".", ",")} € HTVA + TVA {inv.vat_rate} %
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  {inv.total.toFixed(2).replace(".", ",")} €
                </span>
                <button
                  type="button"
                  data-testid={`invoice-download-${inv.number}`}
                  onClick={() => downloadInvoice(inv)}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A5CC8] hover:underline"
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
